import type { ReactNode } from 'react';
import { cn, toneText, type Tone } from '../cn';

export interface KeyValueRowProps {
  k: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  subTone?: Tone;
  valueTone?: Tone;
  trailing?: ReactNode;
  /** Key column width: 84 (status/outputs/uplink) or 140 (security). */
  keyWidth?: 84 | 140;
  /** 11/16 (default) or 14/16 (security rows). */
  tall?: boolean;
  /** Inside a padded card: no horizontal padding. */
  inset?: boolean;
  className?: string;
}

/** Desktop key/value: micro key in a fixed column, value 500 12px/1.3, mono sub 10px/1.4. */
export function KeyValueRow({ k, value, sub, subTone = 'fg4', valueTone = 'fg', trailing, keyWidth = 84, tall, inset, className }: KeyValueRowProps) {
  return (
    <div
      className={cn('grid items-center border-t border-line', tall ? 'gap-4 py-3.5' : 'gap-3 py-[11px]', inset ? 'px-0' : 'px-4', className)}
      style={{ gridTemplateColumns: `${keyWidth}px minmax(0,1fr)${trailing ? ' auto' : ''}` }}
    >
      <span className="micro leading-[1.5] text-fg4">{k}</span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className={cn(tall ? 'text-md font-medium leading-[1.3]' : 'text-base font-medium leading-[1.3]', toneText[valueTone])}>{value}</span>
        {sub && <span className={cn('font-mono font-normal leading-[1.4]', tall ? 'text-sm leading-[1.5]' : 'text-xs', toneText[subTone])}>{sub}</span>}
      </div>
      {trailing}
    </div>
  );
}

export interface FactRowProps {
  label: ReactNode;
  value: ReactNode;
  valueTone?: Tone;
  /** Mono meta style (DISABLED / OFF) vs mono value style (http://10.42.0.1). */
  meta?: boolean;
  inset?: boolean;
  className?: string;
}

/** Simple fact row: 400 12px fg2 label, mono 500 11px fg value (or meta 10px .06em fg3); 9/0 padding inset, 10–11/16 in cards. */
export function FactRow({ label, value, valueTone, meta, inset, className }: FactRowProps) {
  return (
    <div className={cn('flex items-center justify-between gap-3 border-t border-line', inset ? 'px-0 py-[9px]' : 'px-4 py-2.5', className)}>
      <span className="text-base font-normal text-fg2">{label}</span>
      <span className={cn(meta ? 'meta' : 'font-mono text-sm font-medium', valueTone ? toneText[valueTone] : meta ? 'text-fg3' : 'text-fg', 'text-right')}>{value}</span>
    </div>
  );
}
